import { useMemo, useState } from 'react';
import { niceTicks } from '../../utils/spores/metrics';

/*
 * A plate's growth over its petri dish readings (routes/culture_analyses.py
 * growth()): days since the dish was started across, ONE measure up -- the
 * clean colony area (cm2) or its mean radius (mm), switched by the caller's
 * `measure` -- never both on two axes. Hand-drawn SVG, like the spore
 * Histogram; no chart library in this codebase.
 *
 * series: [{key, label, points: [{day, value, contaminated, observedAt}]}],
 * one per plate. Colours are the first four slots of a categorical palette
 * validated for colour-blind separation on both the light and the field_dark
 * surfaces, in fixed order (a plate keeps its colour whatever else is shown);
 * a fifth plate on is listed in the table only, and the chart says so.
 * Identity is never colour alone: two or more plates get a legend and each
 * line its code at its end. A reading where contamination was painted wears
 * a warning mark (an icon and the legend's word for it, not a colour).
 *
 * Hover (or focus) on the plot shows a crosshair on the nearest day and a
 * tooltip with every plate's value there; the same numbers are in the table
 * under the chart for keyboards and screen readers.
 *
 * A series may be a replicate set (GrowthPanel's sets view, utils/culture/
 * replicates.js setBands): its points carry `sd` and `n`, and it is drawn as
 * its mean with a band of ± one SD round it (translucent, in its colour) and
 * a whisker at each point; its tooltip and point titles say "mean ± SD (n)".
 */
export const SERIES_SLOTS = 4;
const LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const DARK = ['#3987e5', '#d95926', '#199e70', '#c98500'];
const CSS = `
.growth-curve { ${LIGHT.map((c, i) => `--gc-${i + 1}: ${c};`).join(' ')} }
:root[data-theme="field_dark"] .growth-curve { ${DARK.map((c, i) => `--gc-${i + 1}: ${c};`).join(' ')} }
`;

const W = 520;
const H = 230;
const PAD = { left: 46, right: 74, top: 14, bottom: 34 };
const plotW = W - PAD.left - PAD.right;
const plotH = H - PAD.top - PAD.bottom;
const fmtNum = v => (Math.abs(v) >= 100 ? Math.round(v).toString() : Number(v.toFixed(2)).toString());
/** A point's value, or a set's mean ± SD and n. */
const valueText = p => (typeof p.sd === 'number' ? `${fmtNum(p.value)} ± ${fmtNum(p.sd)} (n = ${p.n})`
  : (p.n > 1 ? `${fmtNum(p.value)} (n = ${p.n})` : fmtNum(p.value)));

export default function GrowthCurve({ series, unit, label, labels, testId = 'growth-curve' }) {
  const shown = series.slice(0, SERIES_SLOTS);
  const all = shown.flatMap(s => s.points.filter(p => p.value !== null && p.value !== undefined));
  const banded = all.some(p => typeof p.sd === 'number');
  const [hover, setHover] = useState(null);
  const scales = useMemo(() => {
    if (!all.length) return null;
    const maxDay = Math.max(1, ...all.map(p => p.day));
    const maxValue = Math.max(...all.map(p => p.value + (typeof p.sd === 'number' ? p.sd : 0)), 0);
    const xTicks = niceTicks(0, maxDay, 6);
    const yTicks = niceTicks(0, maxValue > 0 ? maxValue : 1, 4);
    const x1 = xTicks[xTicks.length - 1];
    const y1 = yTicks[yTicks.length - 1];
    return {
      xTicks, yTicks,
      x: d => PAD.left + (d / x1) * plotW,
      y: v => PAD.top + plotH - (v / y1) * plotH,
      day: px => ((px - PAD.left) / plotW) * x1,
    };
  }, [all]);
  if (!scales) return <p data-testid={testId} style={emptyStyle}>{labels.empty}</p>;
  const days = [...new Set(all.map(p => p.day))].sort((a, b) => a - b);

  const onMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width) return;
    const day = scales.day(((event.clientX - box.left) / box.width) * W);
    setHover(days.reduce((best, d) => (Math.abs(d - day) < Math.abs(best - day) ? d : best), days[0]));
  };
  const step = (dir) => {
    const at = hover === null ? -1 : days.indexOf(hover);
    setHover(days[Math.min(days.length - 1, Math.max(0, at + dir))]);
  };
  const atHover = hover === null ? [] : shown.map((s, i) => ({ s, i, p: s.points.find(p => p.day === hover) }))
    .filter(r => r.p && r.p.value !== null && r.p.value !== undefined);
  const tipX = hover === null ? 0 : scales.x(hover);
  const tipW = banded ? 210 : 150;
  const tipLeft = tipX + 8 + tipW > W ? tipX - 8 - tipW : tipX + 8;

  return (
    <div className="growth-curve" data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <style>{CSS}</style>
      {shown.length > 1 && (
        <ul aria-label={labels.legend} data-testid="growth-legend" style={legendStyle}>
          {shown.map((s, i) => (
            <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="18" height="10" aria-hidden="true">
                <line x1="1" y1="5" x2="17" y2="5" strokeWidth="2" style={{ stroke: `var(--gc-${i + 1})` }} />
                <circle cx="9" cy="5" r="3" style={{ fill: `var(--gc-${i + 1})` }} />
              </svg>
              <bdi dir="ltr">{s.label}</bdi>
            </li>
          ))}
          {all.some(p => p.contaminated) && <li><span aria-hidden="true">⚠</span> {labels.contaminated}</li>}
          {banded && labels.band && <li data-testid="growth-band-key"><span aria-hidden="true">▒</span> {labels.band}</li>}
        </ul>
      )}
      {shown.length === 1 && all.some(p => p.contaminated) && (
        <p style={{ ...emptyStyle, color: 'var(--c-text-body-fg)' }}><span aria-hidden="true">⚠</span> {labels.contaminated}</p>
      )}
      <svg role="img" aria-label={label} width="100%" viewBox={`0 0 ${W} ${H}`} tabIndex={0}
           style={{ display: 'block', maxWidth: W * 1.4, outline: 'none' }}
           onPointerMove={onMove} onPointerLeave={() => setHover(null)} onBlur={() => setHover(null)}
           onKeyDown={(e) => {
             if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
             if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
             if (e.key === 'Escape') setHover(null);
           }}>
        {scales.yTicks.map(v => (
          <g key={`y${v}`}>
            <line x1={PAD.left} x2={PAD.left + plotW} y1={scales.y(v)} y2={scales.y(v)} strokeWidth={1}
                  style={{ stroke: 'var(--c-border)' }} />
            <text x={PAD.left - 6} y={scales.y(v) + 3} textAnchor="end" fontSize="10"
                  style={{ fill: 'var(--c-text-muted-fg)' }}>{fmtNum(v)}</text>
          </g>
        ))}
        {scales.xTicks.map(d => (
          <text key={`x${d}`} x={scales.x(d)} y={PAD.top + plotH + 14} textAnchor="middle" fontSize="10"
                style={{ fill: 'var(--c-text-muted-fg)' }}>{fmtNum(d)}</text>
        ))}
        <text x={PAD.left + plotW / 2} y={H - 4} textAnchor="middle" fontSize="10"
              style={{ fill: 'var(--c-text-muted-fg)' }}>{labels.days}</text>
        <text x={8} y={PAD.top - 3} fontSize="10" style={{ fill: 'var(--c-text-muted-fg)' }}>{unit}</text>
        {shown.map((s, i) => {
          const pts = s.points.filter(p => p.value !== null && p.value !== undefined);
          const colour = `var(--gc-${i + 1})`;
          const last = pts[pts.length - 1];
          const spread = pts.filter(p => typeof p.sd === 'number');
          const top = spread.map(p => `${scales.x(p.day)},${scales.y(p.value + p.sd)}`);
          const bottom = spread.map(p => `${scales.x(p.day)},${scales.y(Math.max(0, p.value - p.sd))}`).reverse();
          return (
            <g key={s.key} data-testid="growth-series" data-plate={s.key} data-set={s.set ? 'true' : undefined}>
              {spread.length > 1 && (
                <polygon data-testid="growth-band" points={[...top, ...bottom].join(' ')} strokeWidth={0}
                         style={{ fill: colour, fillOpacity: 0.16 }} />
              )}
              {spread.map(p => (
                <line key={`sd-${p.day}`} data-testid="growth-whisker" x1={scales.x(p.day)} x2={scales.x(p.day)}
                      y1={scales.y(p.value + p.sd)} y2={scales.y(Math.max(0, p.value - p.sd))} strokeWidth={1.5}
                      style={{ stroke: colour }} />
              ))}
              {pts.length > 1 && (
                <polyline points={pts.map(p => `${scales.x(p.day)},${scales.y(p.value)}`).join(' ')} fill="none"
                          strokeWidth={2} strokeLinejoin="round" style={{ stroke: colour }} />
              )}
              {pts.map(p => (
                <g key={`${p.day}-${p.observedAt}`} data-testid="growth-point" data-contaminated={p.contaminated ? 'true' : 'false'}>
                  <title>{`${s.label} · ${labels.dayN(p.day)} · ${valueText(p)} ${unit}`}</title>
                  <circle cx={scales.x(p.day)} cy={scales.y(p.value)} r={4} strokeWidth={2}
                          style={{ fill: colour, stroke: 'var(--c-surface)' }} />
                  {p.contaminated && (
                    <text x={scales.x(p.day)} y={scales.y(p.value) - 8} textAnchor="middle" fontSize="11"
                          style={{ fill: 'var(--c-warning-text-fg)' }}>⚠</text>
                  )}
                </g>
              ))}
              {shown.length > 1 && last && (
                <text x={scales.x(last.day) + 7} y={scales.y(last.value) + 3} fontSize="10"
                      style={{ fill: 'var(--c-text-body-fg)' }}>{s.label.length > 12 ? `…${s.label.slice(-11)}` : s.label}</text>
              )}
            </g>
          );
        })}
        {hover !== null && (
          <g data-testid="growth-tooltip" pointerEvents="none">
            <line x1={tipX} x2={tipX} y1={PAD.top} y2={PAD.top + plotH} strokeWidth={1} strokeDasharray="3 3"
                  style={{ stroke: 'var(--c-text-muted-fg)' }} />
            <rect x={tipLeft} y={PAD.top} width={tipW} height={18 + 15 * atHover.length} rx={6}
                  style={{ fill: 'var(--c-surface)', stroke: 'var(--c-border-strong)' }} />
            <text x={tipLeft + 8} y={PAD.top + 14} fontSize="11" fontWeight="700"
                  style={{ fill: 'var(--c-text-fg)' }}>{labels.dayN(hover)}</text>
            {atHover.map(({ s, i, p }, k) => (
              <g key={s.key}>
                <circle cx={tipLeft + 12} cy={PAD.top + 26 + 15 * k} r={3.5} style={{ fill: `var(--gc-${i + 1})` }} />
                <text x={tipLeft + 20} y={PAD.top + 30 + 15 * k} fontSize="10" style={{ fill: 'var(--c-text-body-fg)' }}>
                  {`${s.label.length > 10 ? `…${s.label.slice(-9)}` : s.label}: ${valueText(p)} ${unit}${p.contaminated ? ' ⚠' : ''}`}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
      {series.length > SERIES_SLOTS && <p style={emptyStyle}>{labels.more(series.length - SERIES_SLOTS)}</p>}
    </div>
  );
}

const emptyStyle = { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' };
const legendStyle = { display: 'flex', gap: 12, flexWrap: 'wrap', margin: 0, padding: 0, listStyle: 'none', fontSize: 12,
                      color: 'var(--c-text-body-fg)' };
